import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Button, ListItemText, Menu, MenuItem } from '@mui/material';
import { format, isSameMonth, startOfMonth, subMonths } from 'date-fns';
import { cs } from 'date-fns/locale';
import { useState } from 'react';

const MONTHS_BACK = 24;

const capitalize = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);
const monthLabel = (date: Date) => capitalize(format(date, 'LLLL yyyy', { locale: cs }));

interface DashboardMonthSelectProps {
  value: Date;
  onChange: (month: Date) => void;
}

/** Collapsed calendar button; the list of months opens only after a click. */
export const DashboardMonthSelect = ({ value, onChange }: DashboardMonthSelectProps) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const currentMonth = startOfMonth(new Date());
  const months = Array.from({ length: MONTHS_BACK }, (_, i) => subMonths(currentMonth, i));

  return (
    <>
      <Button
        variant="outlined"
        color="inherit"
        aria-haspopup="listbox"
        aria-expanded={Boolean(anchor)}
        onClick={(event) => setAnchor(event.currentTarget)}
        startIcon={<CalendarMonthOutlinedIcon />}
        endIcon={<KeyboardArrowDownIcon />}
        sx={{
          minHeight: 48,
          paddingX: 2,
          whiteSpace: 'nowrap',
          backgroundColor: 'background.paper',
          borderColor: 'rgba(34, 34, 52, 0.18)',
          '&:hover': { borderColor: 'rgba(34, 34, 52, 0.4)', backgroundColor: 'background.paper' }
        }}
      >
        {monthLabel(value)}
      </Button>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { mt: 1, maxHeight: 360, minWidth: 200 } } }}
      >
        {months.map((month) => (
          <MenuItem
            key={month.toISOString()}
            selected={isSameMonth(month, value)}
            onClick={() => {
              onChange(month);
              setAnchor(null);
            }}
          >
            <ListItemText primary={monthLabel(month)} />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};
